import { getAreas } from '../services/areas-service';
import { getProcesses } from '../services/processes-service';
import { getSystems } from '../services/systems-service';
import { getAssets } from '../services/assets-service';

// Translation key maps — pass through `t(...)` at render time.
export const typeLabelKeys = {
  areas: 'gap-analysis:entityType.areas',
  processes: 'gap-analysis:entityType.processes',
  systems: 'gap-analysis:entityType.systems',
  assets: 'gap-analysis:entityType.assets',
  companies: 'gap-analysis:entityType.companies',
};

export const maturityLevelKeys = {
  notPerformed: 'gap-analysis:maturityLevel.notPerformed',
  performedInformally: 'gap-analysis:maturityLevel.performedInformally',
  plannedTracked: 'gap-analysis:maturityLevel.plannedTracked',
  wellDefined: 'gap-analysis:maturityLevel.wellDefined',
  quantitativelyControlled: 'gap-analysis:maturityLevel.quantitativelyControlled',
  continuouslyImproving: 'gap-analysis:maturityLevel.continuouslyImproving',
};

// New code: pass `labelKey` / `descriptionKey` through `t(...)`.
// Legacy `label` / `description` fields kept as English fallbacks for callers
// that don't yet translate (dashboards, reports, charts).
export const statusControl = {
  noEntities: {
    color: '#9E9E9E',
    label: 'Not Linked',
    description: 'The control is not linked to any specific entity.',
    labelKey: 'gap-analysis:statusControl.noEntities.label',
    descriptionKey: 'gap-analysis:statusControl.noEntities.description',
  },
  noResponse: {
    color: '#757575',
    label: 'No Response',
    description: 'The control has not yet been evaluated and no response has been provided.',
    labelKey: 'gap-analysis:statusControl.noResponse.label',
    descriptionKey: 'gap-analysis:statusControl.noResponse.description',
  },
  deficient: {
    color: '#D32F2F',
    label: 'Deficient',
    description: 'The control is not implemented or is partially implemented.',
    labelKey: 'gap-analysis:statusControl.deficient.label',
    descriptionKey: 'gap-analysis:statusControl.deficient.description',
  },
  implemented: {
    color: '#FF9800',
    label: 'Implemented',
    description: 'The control is implemented but has not yet been tested or verified.',
    labelKey: 'gap-analysis:statusControl.implemented.label',
    descriptionKey: 'gap-analysis:statusControl.implemented.description',
  },
  readyForReview: {
    color: '#FFC107',
    label: 'Ready for Review',
    description: 'Evidence has been collected and the control is ready for review by an assessor.',
    labelKey: 'gap-analysis:statusControl.readyForReview.label',
    descriptionKey: 'gap-analysis:statusControl.readyForReview.description',
  },
  monitored: {
    color: '#4CAF50',
    label: 'Monitored',
    description: 'The control has been evaluated and is in a state of continuous monitoring.',
    labelKey: 'gap-analysis:statusControl.monitored.label',
    descriptionKey: 'gap-analysis:statusControl.monitored.description',
  },
  notApplicable: {
    color: '#72ba76',
    label: 'Not Applicable',
    description: 'The control does not apply to this context or environment.',
    labelKey: 'gap-analysis:statusControl.notApplicable.label',
    descriptionKey: 'gap-analysis:statusControl.notApplicable.description',
  },
};

// Backwards-compatible English fallbacks — used in spots that don't yet pass `t`.
// New code should prefer `t(typeLabelKeys[k])` etc.
export const typeLabels = {
  areas: 'Business Areas',
  processes: 'Processes',
  systems: 'Systems',
  assets: 'Assets',
  companies: 'Company',
};

export const maturityLevels = {
  notPerformed: 'Level 0',
  performedInformally: 'Level 1',
  plannedTracked: 'Level 2',
  wellDefined: 'Level 3',
  quantitativelyControlled: 'Level 4',
  continuouslyImproving: 'Level 5',
};

export const options = [
  { value: 'companies', labelKey: 'gap-analysis:entityType.companies' },
  { value: 'areas', labelKey: 'gap-analysis:entityType.areas', fetchFunction: getAreas },
  { value: 'processes', labelKey: 'gap-analysis:entityType.processes', fetchFunction: getProcesses },
  { value: 'systems', labelKey: 'gap-analysis:entityType.systems', fetchFunction: getSystems },
  { value: 'assets', labelKey: 'gap-analysis:entityType.assets', fetchFunction: getAssets },
];

export const getSolutionKey = (size) => {
  if (size >= 1 && size <= 9) return 'solutionsMicroSmall';
  if (size >= 10 && size <= 49) return 'solutionsSmall';
  if (size >= 50 && size <= 249) return 'solutionsMedium';
  if (size >= 250 && size <= 999) return 'solutionsLarge';
  if (size >= 1000) return 'solutionsEnterprise';
  return null;
};

// Backend may return scfDomain in several historical shapes:
//   - "asset_management"                       (current dataset, snake_case)
//   - "domain.asset_management"                (legacy DB seed, locale-key prefix)
//   - "Asset Management" / "Asset & Management" (older free-text seeds)
// Normalize all of them to the canonical snake_case key used in
// locales/{en,pt,es}/gap-analysis.json under `domain.*`.
export const normalizeDomainKey = (value) => {
  const raw = String(value ?? '').trim().replace(/^domain\./i, '');
  return raw
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export const translateDomain = (t, value) =>
  t(`gap-analysis:domain.${normalizeDomainKey(value)}`, { defaultValue: value });

const lastSegment = (value) =>
  String(value ?? '').toLowerCase().split('.').pop().trim();

export const translateFunctionGrouping = (t, value) =>
  t(`gap-analysis:nistCsfFunctionGrouping.${lastSegment(value)}`, { defaultValue: value });

export const translateApplicability = (t, value) =>
  t(`gap-analysis:applicabilityStatus.${lastSegment(value)}`, { defaultValue: value });

export const getBreadcrumbBase = (pathname, t) => {
  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '');

  const translate = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

  if (normalizedPath.includes('/gap-analysis')) {
    return translate('common:breadcrumbs.gapAnalysis', 'Assessment / Gap Analysis');
  }
  if (normalizedPath.includes('/reports/compliance-program')) {
    return translate('common:breadcrumbs.reportsComplianceProgram', 'Reports / Privacy and Data Protection Program');
  }
  if (normalizedPath.includes('/compliance-program/') && normalizedPath.includes('/vincular')) {
    return translate('common:breadcrumbs.manage', 'Manage');
  }
  if (normalizedPath.includes('/compliance-program')) {
    return translate('common:breadcrumbs.manageComplianceProgram', 'Manage / Privacy and Data Protection Program');
  }

  return translate('common:breadcrumbs.home', 'Home');
};
